import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { DashboardChartData } from '@app/areas/dashboard/models/chart.model';
import { currency } from '@app/areas/dashboard/utils/format.util';

/**
 * Chart.js graph. Renders either a stacked bar (coalition location spend) or a
 * line (per-merchant income drill-down). Reads theme via CSS variables so no
 * theme object needs to be threaded through.
 */
const customCanvasBackgroundColorPlugin = {
  id: 'customCanvasBackgroundColor',
  beforeDraw: (chart: any, _args: any, options: any) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = options.color || '#eff3f9';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

const chartAreaBorderPlugin = {
  id: 'chartAreaBorder',
  beforeDraw(chart: any, _args: any, options: any) {
    const {
      ctx,
      chartArea: { left, top, width, height },
    } = chart;
    ctx.save();
    ctx.strokeStyle = options.borderColor;
    ctx.lineWidth = options.borderWidth;
    ctx.strokeRect(left, top, width, height);
    ctx.restore();
  },
};

@Component({
  selector: 'app-dashboard-graph',
  templateUrl: './dashboard-graph.component.html',
  styleUrls: ['./dashboard-graph.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgIf, ChartModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardGraphComponent implements OnChanges {
  @Input() data$: Observable<DashboardChartData> | null = null;
  @Input() type: 'bar' | 'line' = 'bar';
  @Input() xTitle = '';
  @Input() yTitle = 'Sales ($)';
  @Input() showLegend = true;

  @Output() dataSelect = new EventEmitter<{ index: number; datasetIndex: number }>();

  plugins: any[] = [customCanvasBackgroundColorPlugin, chartAreaBorderPlugin];
  options: any;

  onDataSelect(event: { element?: { index?: number; datasetIndex?: number } }): void {
    const index = event?.element?.index;
    const datasetIndex = event?.element?.datasetIndex;
    if (index != null && datasetIndex != null) {
      this.dataSelect.emit({ index, datasetIndex });
    }
  }

  ngOnChanges(): void {
    const stacked = this.type === 'bar';
    const canvasBg = this.cssVar('--dashboard-card-bg', '#eff3f9');

    this.options = {
      maintainAspectRatio: false,
      aspectRatio: 0.7,
      plugins: {
        customCanvasBackgroundColor: { color: canvasBg },
        chartAreaBorder: { borderColor: '#ccd3dd', borderWidth: 1.5 },
        legend: {
          display: this.showLegend,
          labels: { useBorderRadius: true, borderRadius: 5, boxWidth: 20, boxHeight: 20 },
          position: 'top',
          align: 'end',
        },
        tooltip:
          this.type === 'line'
            ? {
                callbacks: {
                  // Drill-down line: no date-range title and no "Amount:"/"Comparison:"
                  // prefix — just the amount, plus the comparison series' own date
                  // (aligned series otherwise share the primary's x-axis labels).
                  title: () => '',
                  label: (ctx: any) => {
                    const value = currency(ctx.parsed?.y ?? 0);
                    const pointLabel = ctx.dataset?.pointLabels?.[ctx.dataIndex];
                    return pointLabel ? `${pointLabel}: ${value}` : value;
                  },
                },
              }
            : {},
      },
      elements: { point: { radius: 5 } },
      scales: {
        x: {
          stacked,
          title: { display: !!this.xTitle, text: this.xTitle, color: '#607389', font: { size: 16, weight: 500 } },
          ticks: { color: '#607389', font: { size: 14, weight: 500 } },
          grid: { color: '#ccd3dd', drawBorder: true },
          offset: true,
        },
        y: {
          stacked,
          beginAtZero: true,
          title: { display: !!this.yTitle, text: this.yTitle, color: '#607389', font: { size: 16, weight: 500 } },
          ticks: { color: '#607389', font: { size: 14, weight: 500 }, min: 0, beginAtZero: true },
          grid: { color: '#ccd3dd', drawBorder: true },
        },
      },
    };
  }

  private cssVar(name: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }
}
